import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { docsStoryOrderApi } from '../../api/docsStoryOrder';
import { applyDocsStoryOrderSpine } from '../../utils/applyDocsStoryOrder';
import PromptSignatureSoftNotice from './PromptSignatureSoftNotice';
import {
  STORY_ORDER_APPLIED,
  STORY_ORDER_APPLY_ERROR,
  STORY_ORDER_EDIT_IN_BUILDER,
  STORY_ORDER_GAP,
  STORY_ORDER_NEED_BOOK,
  STORY_ORDER_OMITTED,
  STORY_ORDER_READ_ERROR,
  STORY_ORDER_SPINE_HEADING,
  STORY_ORDER_SPINE_LEDE,
  STORY_ORDER_SPINE_NOT_READY,
  STORY_ORDER_THIN,
  STORY_ORDER_USE_SPINE,
} from '../../utils/writerCopy';

/**
 * Sibling to Make notes (#1242). Same checklist of selected Docs.
 * Offer 1 creates a custom Framework and sets it on the book.
 * Offer 2 opens Framework Builder with those scenes already in the editor.
 * Neither writes a Workshop fill.
 */
export default function DocsStoryOrderOffers({ projectId, storyId, batchIds }) {
  const navigate = useNavigate();
  const [preview, setPreview] = useState(null);
  const [readError, setReadError] = useState(false);
  const [busy, setBusy] = useState(false);
  const [applied, setApplied] = useState(false);
  const [error, setError] = useState(null);
  const batchKey = (batchIds || []).join(',');

  useEffect(() => {
    if (!projectId || !batchKey) {
      setPreview(null);
      setReadError(false);
      return undefined;
    }
    let cancelled = false;
    setApplied(false);
    setError(null);
    docsStoryOrderApi.preview(projectId, { batchIds: batchKey.split(',') })
      .then((data) => {
        if (!cancelled) {
          setPreview(data || null);
          setReadError(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setPreview(null);
          setReadError(true);
        }
      });
    return () => { cancelled = true; };
  }, [projectId, batchKey]);

  const useSpine = async () => {
    if (!preview?.spine || !storyId || busy) return;
    setBusy(true);
    try {
      await applyDocsStoryOrderSpine({ storyId, spine: preview.spine });
      setApplied(true);
      setError(null);
    } catch (err) {
      setError(err?.message || STORY_ORDER_APPLY_ERROR);
    } finally {
      setBusy(false);
    }
  };

  const editInBuilder = () => {
    if (!preview?.editor_seed) return;
    const seed = preview.editor_seed;
    navigate('/frameworks', {
      state: {
        docsSpineSeed: {
          framework_name: seed.framework_name,
          description: seed.description || '',
          genre_tag: seed.genre_tag || null,
          thin_genre: false,
          beats: seed.beats || [],
          acts: seed.acts || [],
          tracks: [],
          tropes: [],
        },
      },
    });
  };

  if (!batchKey) return null;
  if (readError) {
    return <p className="so-lede">{STORY_ORDER_READ_ERROR}</p>;
  }
  if (!preview?.present) {
    const withheld = Boolean(
      preview?.structure_withheld || preview?.prompt_signature === 'mismatch'
    );
    if (!withheld) return null;
    return <PromptSignatureSoftNotice />;
  }

  const gapCount = preview.gap_count || 0;
  const canUse = Boolean(preview.offer1_ready && storyId && !applied);

  return (
    <section className="so-section" aria-label={STORY_ORDER_SPINE_HEADING}>
      <h2>{STORY_ORDER_SPINE_HEADING}</h2>
      <p className="so-lede">{STORY_ORDER_SPINE_LEDE}</p>
      <p className="so-lede">{STORY_ORDER_SPINE_NOT_READY}</p>
      {!storyId && <p className="so-lede">{STORY_ORDER_NEED_BOOK}</p>}
      {gapCount > 0 && preview.offer1_ready && <p className="so-lede">{STORY_ORDER_GAP}</p>}
      {!preview.offer1_ready && <p className="so-lede">{STORY_ORDER_THIN}</p>}
      {preview.omitted_count > 0 && <p className="so-lede">{STORY_ORDER_OMITTED}</p>}
      <ul className="so-list">
        {(preview.scenes || []).map((scene, index) => (
          <li key={`${scene.act}-${index}-${scene.name}`}>
            <strong>{scene.name}</strong>
            {scene.purpose
              ? <em>{scene.purpose}</em>
              : <em>No purpose line. Left as a gap.</em>}
          </li>
        ))}
      </ul>
      {applied && <p className="so-lede">{STORY_ORDER_APPLIED}</p>}
      {error && <p className="so-lede" role="alert">{error}</p>}
      <div className="so-actions">
        <button
          type="button"
          className="bs-btn bs-btn--primary"
          onClick={useSpine}
          disabled={!canUse || busy}
        >
          {busy ? 'Saving…' : STORY_ORDER_USE_SPINE}
        </button>
        <button
          type="button"
          className="bs-btn bs-btn--secondary"
          onClick={editInBuilder}
          disabled={busy || !preview.editor_seed}
        >
          {STORY_ORDER_EDIT_IN_BUILDER}
        </button>
      </div>
    </section>
  );
}
